import DownloadIcon from '@mui/icons-material/Download';
import { IconButton, Tooltip } from '@mui/material';
import Excel from 'exceljs';
import FileSaver from 'file-saver';
import React from 'react';
import { autoSizeColumns } from '../../../../../../helpers/excel-utils';

export interface AccountLedgerExportRow {
    tipo: string;
    folio: string;
    fecha: string;
    notas: string;
    total: number | null;
    transferido: number | null;
    acumulado: number;
}

export interface AccountLedgerExportSummary {
    balance: number;
}

export interface AccountExpenseExportGroup {
    folio: string;
    fecha: string;
    notas: string;
    total: number;
    resources: Array<{
        name: string;
        units: number;
        unitPrice: number;
        notes: string;
    }>;
}

const MONEY_FMT = '#,##0.00';

export function AccountLedgerExcelExportButton({
    mainRows,
    expenseGroups,
    summary,
    fileName,
    disabled,
}: {
    mainRows: AccountLedgerExportRow[];
    expenseGroups: AccountExpenseExportGroup[];
    summary: AccountLedgerExportSummary;
    fileName: string;
    disabled?: boolean;
}) {
    return (
        <Tooltip title="Descargar Excel">
            <span>
                <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={async () => {
                        const workbook = new Excel.Workbook();

                        const totalsSheet = workbook.addWorksheet('Totales');
                        const balanceRow = totalsSheet.addRow([
                            'Balance',
                            summary.balance,
                        ]);
                        balanceRow.font = { bold: true };
                        totalsSheet.getColumn(2).numFmt = MONEY_FMT;
                        totalsSheet.getColumn(2).alignment = {
                            horizontal: 'right',
                        };
                        autoSizeColumns(totalsSheet);

                        const expensesSheet = workbook.addWorksheet('Entregas');
                        expensesSheet.addRow([
                            'Tipo',
                            'Folio',
                            'Fecha',
                            'Recurso',
                            'Unidades',
                            'Precio unitario',
                            'Importe',
                            'Notas',
                        ]);
                        expenseGroups.forEach((expense) => {
                            const expenseRow = expensesSheet.addRow([
                                'Gasto',
                                expense.folio,
                                expense.fecha,
                                '',
                                null,
                                null,
                                expense.total,
                                expense.notas,
                            ]);
                            expenseRow.font = { bold: true };
                            expense.resources.forEach((resource) => {
                                const resourceRow = expensesSheet.addRow([
                                    '',
                                    '',
                                    '',
                                    resource.name,
                                    resource.units,
                                    resource.unitPrice,
                                    resource.units * resource.unitPrice,
                                    resource.notes,
                                ]);
                                resourceRow.outlineLevel = 1;
                            });
                        });
                        [5, 6, 7].forEach((index) => {
                            const column = expensesSheet.getColumn(index);
                            column.numFmt = MONEY_FMT;
                            column.alignment = { horizontal: 'right' };
                        });
                        autoSizeColumns(expensesSheet);

                        const ledgerSheet = workbook.addWorksheet('Transferidos');
                        ledgerSheet.addRow([
                            'Tipo',
                            'Folio',
                            'Fecha',
                            'Notas',
                            'Total',
                            'Transferido',
                            'Acumulado',
                        ]);
                        mainRows.forEach((row) => {
                            ledgerSheet.addRow([
                                row.tipo,
                                row.folio,
                                row.fecha,
                                row.notas,
                                row.total,
                                row.transferido,
                                row.acumulado,
                            ]);
                        });
                        [5, 6, 7].forEach((index) => {
                            const column = ledgerSheet.getColumn(index);
                            column.numFmt = MONEY_FMT;
                            column.alignment = { horizontal: 'right' };
                        });
                        autoSizeColumns(ledgerSheet);

                        const buffer = await workbook.xlsx.writeBuffer();
                        FileSaver.saveAs(new Blob([buffer]), fileName);
                    }}
                >
                    <DownloadIcon fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );
}
